import "../../shared/academy-typography.css";
import "./styles/fixed-navigation.css";
import "./styles/contained-controls.css";
import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import SecureAccessApp from "./app/SecureAccessApp";
import { loadAcademySession } from "../../shared/academy-session";
import { mountFrontend } from "./app/bootstrap";
import { APP_ID, appOrigin } from "./config/environment";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import "./styles/auth-magic.css";
import "./styles/account-fit.css";
import "./styles/verify-email.css";
import "./styles/application-fit.css";
import { safeLoadState } from "./data/mockData";
import { readAuthorizationClientId } from "./services/authorization";
import { accountJourneyScreen } from "./services/accessRoutes";
import { roleById } from "./utils/auth";

const root = document.getElementById("root");

if (!root) {
  throw new Error("Unable to find the root element");
}

const runSessionCheck = () => {
  const state = safeLoadState();
  const roleId = state.onboarding.roleId || state.application.roleId;
  const role = roleById(roleId);
  const clientId = readAuthorizationClientId();
  const message = {
    source: "chatoner-academy-session-check",
    authenticated: Boolean(state.authenticated),
    completed: accountJourneyScreen(state) === "complete",
    roleId,
    roleCategory: role.category,
    clientId,
  };
  try {
    window.parent.postMessage(message, "*");
  } catch {
    /* posting to the host page may be unavailable */
  }
  root.removeAttribute("aria-busy");
  const fallback = document.createElement("div");
  fallback.dataset.sessionCheck = "true";
  root.append(fallback);
};

const requestedClient = readAuthorizationClientId();
// Direct visits to Academy Access serve the canonical landing and account journey
// (App). The real-backend secure sign-in is reserved for the learner and parent
// role handoffs that redirect here with an explicit client_id.
const secureAccess = requestedClient === "learners" || requestedClient === "parents";
if (window.location.pathname.replace(/\/+$/, "") === "/session-check" && secureAccess) {
  // This status probe contains no credential or identity payload. Its recipient is allowlisted.
  void loadAcademySession()
    .then(session => {
      const target = new URL(document.referrer).origin;
      const allowed = [appOrigin("learners"), appOrigin("parents"), "https://academy.chatoner.com"];
      if (allowed.includes(target))
        window.parent.postMessage(
          {
            source: "chatoner-academy-session-check",
            authenticated: Boolean(session.user?.emailVerified && !session.requiresMfa),
            completed: Boolean(
              session.user?.emailVerified &&
              !session.requiresMfa &&
              session.workspaces.some(workspace => workspace.status === "active"),
            ),
          },
          target,
        );
    })
    .catch(() => {
      /* An unverified session never reports successful sign-in. */
    })
    .finally(() => {
      root.removeAttribute("aria-busy");
    });
} else if (window.location.pathname.replace(/\/+$/, "") === "/session-check") {
  runSessionCheck();
} else if (secureAccess) {
  root.removeAttribute("aria-busy");
  createRoot(root).render(
    <React.StrictMode>
      <AppErrorBoundary>
        <SecureAccessApp />
      </AppErrorBoundary>
    </React.StrictMode>,
  );
} else {
  void mountFrontend(
    root,
    <React.StrictMode>
      <AppErrorBoundary>
        <App />
      </AppErrorBoundary>
    </React.StrictMode>,
    APP_ID,
  );
}
